/**
 * Gibson Homewares custom JavaScript.
 *
 * Cart drawer suggestions ("Goes well with this").
 * snippets/mini-cart.liquid renders an empty box with the product ids in the
 * cart and the id of the most recently added product. This script fills it
 * from Shopify's product recommendations endpoint: complementary products
 * first (set up in the Search & Discovery app), then related products if
 * fewer than two complementary ones come back. Products already in the cart
 * are skipped. Single variant products get an Add button; products with
 * options link to their page so the shopper can pick a color or size.
 * The theme re-renders the drawer after every cart change, which replaces
 * the box, so a MutationObserver fills it again each time.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-gibson-upsell]';

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/<[^>]+>/g, '');
  }

  function imageUrl(product, width) {
    var src = product.featured_image || (product.images && product.images[0]);
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function firstAvailableVariant(product) {
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return null;
  }

  function render(box, products) {
    var list = box.querySelector('.gibson-cart-upsell__list');
    if (!list || products.length === 0) {
      box.hidden = true;
      return;
    }

    list.innerHTML = products.map(function (product) {
      var variant = firstAvailableVariant(product);
      var canQuickAdd = product.variants.length === 1 && variant;
      var price = variant ? variant.price : product.price;
      var action = canQuickAdd
        ? '<button type="button" class="gibson-cart-upsell__add" data-variant-id="' + variant.id + '">Add</button>'
        : '<a href="' + escapeHtml(product.url) + '" class="gibson-cart-upsell__add">Options</a>';

      return (
        '<div class="gibson-cart-upsell__item">' +
          '<a href="' + escapeHtml(product.url) + '" class="gibson-cart-upsell__image">' +
            '<img src="' + escapeHtml(imageUrl(product, 200)) + '" alt="" loading="lazy" width="80" height="80">' +
          '</a>' +
          '<div class="gibson-cart-upsell__info">' +
            '<a href="' + escapeHtml(product.url) + '" class="gibson-cart-upsell__name">' + escapeHtml(product.title) + '</a>' +
            '<span class="gibson-cart-upsell__price">' + formatMoney(price) + '</span>' +
          '</div>' +
          action +
        '</div>'
      );
    }).join('');

    box.hidden = false;
  }

  function fetchRecommendations(productId, intent) {
    var base = (window.routes && window.routes.productRecommendationsUrl) || '/recommendations/products';
    var url = base + '.json?product_id=' + encodeURIComponent(productId) + '&limit=8&intent=' + intent;
    return fetch(url)
      .then(function (response) { return response.ok ? response.json() : { products: [] }; })
      .catch(function () { return { products: [] }; });
  }

  function fill(box) {
    var productId = box.getAttribute('data-last-product-id');
    if (!productId) {
      box.hidden = true;
      return;
    }

    var inCart = (box.getAttribute('data-product-ids') || '').split(',');
    var key = productId + '|' + inCart.join(',');
    if (box.getAttribute('data-loaded-key') === key) return;
    box.setAttribute('data-loaded-key', key);

    function usable(product) {
      return product.available && inCart.indexOf(String(product.id)) === -1;
    }

    fetchRecommendations(productId, 'complementary')
      .then(function (data) {
        var products = (data.products || []).filter(usable);
        if (products.length >= 2) return products;
        return fetchRecommendations(productId, 'related').then(function (more) {
          var extra = (more.products || []).filter(function (p) {
            return usable(p) && !products.some(function (q) { return q.id === p.id; });
          });
          return products.concat(extra);
        });
      })
      .then(function (products) { render(box, products.slice(0, 4)); })
      .catch(function () { box.hidden = true; });
  }

  function fillAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var boxes = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < boxes.length; i++) fill(boxes[i]);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('.gibson-cart-upsell__add[data-variant-id]');
    if (!button) return;
    event.preventDefault();
    button.disabled = true;
    button.textContent = 'Adding';

    fetch((window.routes && window.routes.cartAddUrl) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(button.getAttribute('data-variant-id')), quantity: 1 }] })
    })
      .then(function (response) { return response.ok ? response.json() : Promise.reject(response); })
      .then(function () {
        document.documentElement.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { quantity: 1 }
        }));
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = 'Add';
      });
  });

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        if (added[j].nodeType !== 1) continue;
        if (added[j].matches && added[j].matches(SELECTOR)) fill(added[j]);
        else if (added[j].querySelector && added[j].querySelector(SELECTOR)) fillAll(added[j]);
      }
    }
  });

  function start() {
    fillAll(document);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
